import React, { useContext, useState } from 'react'
import './index.scss'
import SspContext from 'selfServicePortal/contexts/SspContext'
import { ICreditBalance } from 'selfServicePortal/interfaces/i-api-creaditCard'
import { updateAgreementConditionService } from 'selfServicePortal/services/CreditCardServices/updateAgreementConditionService'
import appConfig from 'app.config'
import Switch from 'react-switch'
import Modal from 'react-bootstrap/Modal'
import { BoxInfo, CircleSpinner } from 'shared'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

const productId = (
	{
		no: 'BBF.MC.CRDACC_P1',
		de: 'tfdeu-iss-account',
		at: 'tfdeu-iss-account_austria',
	} as Record<string, string>
)[appConfig.country]

export function PpiDe() {
	const { overviewData } = useContext(SspContext)
	const { hasPPI, ppiEligible } = overviewData as ICreditBalance
	const [showModal, setShowModal] = useState(false)
	const [success, setSucces] = useState(false)
	const { t } = useTranslation()

	if (!ppiEligible) {
		return (
			<div className="ppi-de">
				<div className="outer-container">
					<BoxInfo css={'info'} message={t('PpiNotEligibleInfo')} />
				</div>
			</div>
		)
	}

	if (!hasPPI && success) {
		return (
			<div className="ppi-de">
				<div className="outer-container">
					<BoxInfo
						css={'success'}
						message={
							'Zahlungsschutzversicherung erfolgreich aktiviert.'
						}
					/>
				</div>
			</div>
		)
	}
	if (hasPPI && success) {
		return (
			<div className="ppi-de">
				<div className="outer-container">
					<BoxInfo
						css={'success'}
						message={
							'Zahlungsschutzversicherung deaktiviert. Sie können den Versicherungsschutz jederzeit wieder aktivieren und von den Vorteilen profitieren.'
						}
					/>
				</div>
			</div>
		)
	}

	return (
		<div className="ppi-de">
			<div className="outer-container">
				<div>
					<h4>Zahlungsschutzversicherung</h4>
					<p>
						Mit der Zahlungsschutzversicherung können Sie sich gegen
						Arbeitslosigkeit, Arbeitsunfähigkeit oder den Todesfall
						absichern. Es gelten die{' '}
						<a href="https://tfbank.de/media/x02jrxj5/vertragsbedingungen-restschuldversicherung-fur-kreditkarten-de_ob_v3rebranding.pdf">
							Vertragsbedingungen
						</a>{' '}
						der Versicherung. Der Monatsbeitrag beträgt 0,72% der
						Saldoforderung der Kreditkartenabrechnung. Aufgrund
						Ihrer zuvor gemachten Angaben empfehlen wir Ihnen den
						Abschluss der Versicherung, siehe{' '}
						<a href="https://tfbank.de/media/t5yej3ow/7-beratungsprotokoll_ohnekreuz_rebranding.pdf">
							Beratungsprotokoll
						</a>
						. Ich bestätige, dass ich mich derzeit in einem der
						folgenden Arbeitsverhältnisse befinde:
						Vollzeitbeschäftigung, Teilzeit (mindestens 50 %),
						Beamter, Selbstständig erwerbstätig. Ich bestätige
						außerdem, dass mein Arbeitsvertrag nicht gekündigt ist
						oder dass ich in den letzten 12 Monaten nicht mehr als
						30 aufeinanderfolgende Krankheitstage hatte. Durch meine
						Einwilligung bestätige ich, dass ich abgesichert sein
						möchte und mich dafür entscheide, die Versicherung
						kostenpflichtig abzuschließen und ein Versicherter des
						Gruppenversicherungsvertrags von AMTrust International
						Underwriters DAC zu werden.
					</p>
				</div>

				<div className="foot">
					{hasPPI ? (
						<small>Der Versicherungsschutz ist aktiv</small>
					) : (
						<small>
							Achtung: Der Versicherungsschutz ist aktuell nicht
							aktiv und Sie sind gegen die Risiken der
							Arbeitsunfähigkeit, Arbeitslosigkeit und Tod nicht
							abgesichert
						</small>
					)}
					<div className="switch-container">
						<span className="switch-label">
							Versicherungsschutz kostenpflichtig aktivieren
						</span>
						<Switch
							onChange={() => setShowModal(true)}
							checked={hasPPI}
							uncheckedIcon={false}
							checkedIcon={false}
							onColor="#de996f"
							width={64}
							height={32}
						/>
						<span className="switch-label">
							Versicherungsschutz deaktivieren
						</span>
					</div>
				</div>
			</div>

			<EnableModal
				show={!hasPPI && showModal}
				onHide={() => setShowModal(false)}
				onSuccess={() => {
					setSucces(true)
					setShowModal(false)
				}}
			/>
			<DisableModal
				show={hasPPI && showModal}
				onHide={() => setShowModal(false)}
				onSuccess={() => {
					setSucces(true)
					setShowModal(false)
				}}
			/>
		</div>
	)
}

interface ModalProps {
	show: boolean
	onHide: () => void
	onSuccess: () => void
}

function EnableModal({ show, onHide, onSuccess }: ModalProps) {
	const { overviewData, customerData } = useContext(SspContext)
	const { accountAgreementId } = overviewData as ICreditBalance
	const customerId = customerData?.customerId
	const enablePpiMutation = useMutation(
		() =>
			updateAgreementConditionService({
				partyId: customerId!,
				accountAgreementId,
				accountConfiguration: {
					productId,
					conditionValue: {
						conditionIndex: 9040,
						baseAmount: 1,
						currency: 'NON',
					},
				},
			}),
		{ onSuccess },
	)

	return (
		<Modal show={show} onHide={onHide} centered>
			<Modal.Header>
				<h5>{'Zahlungsschutzversicherung aktivieren'}</h5>
			</Modal.Header>

			{enablePpiMutation.isLoading ? (
				<Modal.Body>
					<CircleSpinner />
				</Modal.Body>
			) : (
				<>
					<Modal.Body>
						{
							'Durch meine Einwilligung bestätige ich, dass ich abgesichert sein möchte und mich dafür entscheide, die Versicherung kostenpflichtig abzuschließen und ein Versicherter des Gruppenversicherungsvertrags von AMTrust International Underwriters DAC zu werden.'
						}
					</Modal.Body>
					<Modal.Footer style={{ justifyContent: 'space-between' }}>
						<button
							type="button"
							className="btn btn-primary"
							onClick={() => enablePpiMutation.mutate()}>
							{'Ja'}
						</button>
						<button
							type="button"
							className="btn btn-primary"
							onClick={onHide}>
							{'Nein'}
						</button>
					</Modal.Footer>
				</>
			)}
		</Modal>
	)
}

function DisableModal({ show, onHide, onSuccess }: ModalProps) {
	const { overviewData, customerData } = useContext(SspContext)
	const { accountAgreementId } = overviewData as ICreditBalance
	const customerId = customerData?.customerId
	const disablePpiMutation = useMutation(
		() =>
			updateAgreementConditionService({
				partyId: customerId!,
				accountAgreementId,
				accountConfiguration: {
					productId,
					conditionValue: {
						conditionIndex: 9040,
						baseAmount: 0,
						currency: 'NON',
					},
				},
			}),
		{ onSuccess },
	)

	return (
		<Modal show={show} onHide={onHide} centered>
			<Modal.Header>
				<h5>{'Zahlungsschutzversicherung deaktivieren'}</h5>
			</Modal.Header>

			{disablePpiMutation.isLoading ? (
				<Modal.Body>
					<CircleSpinner />
				</Modal.Body>
			) : (
				<>
					<Modal.Body>
						{
							'Möchten Sie Ihre Zahlungsschutzversicherung wirklich deaktivieren und damit Ihre Absicherung gegen Arbeitslosigkeit, Arbeitsunfähigkeit oder den Todesfall aufgeben?'
						}
					</Modal.Body>
					<Modal.Footer style={{ justifyContent: 'space-between' }}>
						<button
							type="button"
							className="btn btn-primary"
							onClick={() => disablePpiMutation.mutate()}>
							{'Ja'}
						</button>
						<button
							type="button"
							className="btn btn-primary"
							onClick={onHide}>
							{'Nein'}
						</button>
					</Modal.Footer>
				</>
			)}
		</Modal>
	)
}
